import React, { useEffect, useState } from 'react';
import { Alert, Card, Container, Spinner } from 'react-bootstrap';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';

function FactoryVerifyAuditor() {
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Hard-code the API URL to ensure it's correct
  const BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const token = query.get('token');
    
    if (!token) {
      setLoading(false);
      setSuccess(false);
      setMessage('Invalid verification link. Token is missing.');
      return;
    }

    // Prevent duplicate verification attempts
    if (verificationAttempted) {
      return;
    }

    const verifyToken = async () => {
      try {
        setLoading(true);
        
        // Mark that verification has been attempted to prevent duplicates
        setVerificationAttempted(true);
        
        
        // Use fetch instead of axios
        const response = await fetch(`${BASE_URL}/verify-factory-auditor?token=${token}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });
        
        // Parse the JSON response
        const data = await response.json();
        
        
        // Check for success response
        if (response.ok && data && data.success) {
          setSuccess(true);
          setMessage(data.message || 'Your auditor account has been successfully verified!');
          
          // Store successful verification in localStorage
          localStorage.setItem('lastSuccessfulAuditorVerification', Date.now().toString());
          localStorage.setItem('lastVerifiedAuditorToken', token);
          
          // Clear this after 30 minutes
          setTimeout(() => {
            localStorage.removeItem('lastSuccessfulAuditorVerification');
            localStorage.removeItem('lastVerifiedAuditorToken');
          }, 30 * 60 * 1000);
        } else {
          // Handle non-success but valid responses
          setSuccess(false);
          setMessage(data?.message || 'Verification failed');
        }
      } catch (error) {
        console.error('Verification error:', error);
        
        // Special case: if verification fails
        // Check local storage to see if this might be a reused token
        const lastVerificationTimestamp = localStorage.getItem('lastSuccessfulAuditorVerification');
        const lastToken = localStorage.getItem('lastVerifiedAuditorToken');
        
        if (lastToken === token && lastVerificationTimestamp) {
          // This token was already successfully used
          setSuccess(true);
          setMessage('Your auditor account has already been successfully verified. You can now log in.');
          setLoading(false);
          return;
        }
        
        setSuccess(false);
        setMessage('Verification failed');
        setErrorDetails(`Error: ${error.message || 'Unknown error occurred'}`);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [location, BASE_URL, verificationAttempted]);

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <Card className="verification-card border-0 shadow-sm" style={{ maxWidth: '500px', width: '100%' }}>
        <Card.Body className="p-5">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted mb-0">Verifying your auditor account...</p>
            </div>
          ) : (
            <div className="text-center">
              {success ? (
                <>
                  <div className="verification-icon success mb-4">
                    <FaCheckCircle size={60} className="text-success" />
                  </div>
                  <h2 className="mb-3">Verification Successful!</h2>
                  <p className="mb-4">{message}</p>
                  
                </>
              ) : (
                <>
                  <div className="verification-icon error mb-4">
                    <FaTimesCircle size={60} className="text-danger" />
                  </div>
                  <h2 className="mb-3">Verification Failed</h2>
                  <Alert variant="danger">{message}</Alert>
                  {errorDetails && (
                    <Alert variant="info" className="mt-3 mb-3">
                      <small>{errorDetails}</small>
                    </Alert>
                  )}
                  <p className="mt-3">
                    If you continue to experience issues, please contact your administrator.
                  </p>
                </>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <style>{`
        .verification-card {
          border-radius: 15px;
          transition: all 0.3s ease;
        }
        .verification-icon {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          width: 100px;
          height: 100px;
          border-radius: 50%;
        }
        .verification-icon.success {
          background-color: rgba(40, 167, 69, 0.1);
        }
        .verification-icon.error {
          background-color: rgba(220, 53, 69, 0.1);
        }
      `}</style>
    </Container>
  );
}

export default FactoryVerifyAuditor;